import { AfterViewInit, Component, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { CommonModule, NgIf } from '@angular/common';
import { AuthService } from '../auth.service';
import { finalize, take, tap } from 'rxjs';
import { Router, RouterModule } from '@angular/router';
import { environment } from '../../../environments/environment';
import { LocalStorageService } from '../../shared/local-storage/local-storage.service';
import { UserInfo } from '../schemas';
import Keycloak from 'keycloak-js';
@Component({
    selector: 'app-login',
    standalone: true,
    imports: [ReactiveFormsModule, ButtonModule, InputTextModule, PasswordModule, NgIf, RouterModule, CommonModule],
    providers: [AuthService],
    templateUrl: './login.component.html',
    styleUrl: './login.component.scss',
})
export class LoginComponent implements AfterViewInit {
    private router: Router = inject(Router);

    form: FormGroup<{ phone: FormControl<string>; password: FormControl<string>; otp: FormControl<string> }>;
    loginInProgress = false;
    shouldDisplayOtp = false;
    shouldDisplayForm: boolean = environment.isPhoneLoginEnabled;



    constructor(
        private readonly fb: FormBuilder,
        private readonly authService: AuthService,
        private localStorageService: LocalStorageService,
    ) {
        this.form = this.fb.nonNullable.group({
            phone: ['', [Validators.required, Validators.pattern('^5[0-9]{8}$')]],
            password: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]],
            otp: ['', [Validators.minLength(6), Validators.maxLength(6)]],
        });

        const accessToken = this.localStorageService.getAccessToken();
        if (accessToken && !this.authService.isJwtExpired(accessToken, 0)) {
            this.router.navigate(['/providers']);
        }
    }

    ngAfterViewInit(): void {
        this.authService.keycloak.init({ checkLoginIframe: false, pkceMethod: 'S256' }).then((authenticated) => {
            if (authenticated && this.authService.keycloak.idToken) {
                this.handleKeycloakToken(this.authService.keycloak);
            }
        });
    }

    loginWithKeycloak(): void {
        this.authService.keycloak.login({ redirectUri: window.location.href });
    }

    requestOtp() {
        this.loginInProgress = true;
        const { password } = this.form.getRawValue();
        const phone = `00966${this.form.controls.phone.value}`;
        this.authService
            .requestLoginOtp({ phone, password, otp_type: 'login' })
            .pipe(
                take(1),
                tap(() => {
                    this.shouldDisplayOtp = true;
                    this.form.controls.otp.addValidators(Validators.required);
                }),
                finalize(() => {
                    this.loginInProgress = false;
                }),
            )
            .subscribe();
    }

    validateOtp() {
        this.loginInProgress = true;
        const { password, otp } = this.form.getRawValue();
        const phone = `00966${this.form.controls.phone.value}`;
        this.authService
            .loginUser({ password }, otp, phone)
            .pipe(
                take(1),
                tap((response) => {
                    this.localStorageService.setTokens(response);
                    this.authService.isLoggedIn.next(true);
                    this.authService.setUserContext(response.access_token);
                    const decodedToken = this.authService.decodeAndParseJWTToken(response.access_token);
                    const userInfo: UserInfo = {
                        name: decodedToken['display_name'],
                        phone: decodedToken['phone'],
                        email: decodedToken['email'],
                    };
                    this.localStorageService.setUserInfo(userInfo);
                    this.router.navigate(['/providers']);
                }),
                finalize(() => {
                    this.loginInProgress = false;
                }),
            )
            .subscribe();
    }

    private handleKeycloakToken(keycloakInstance: Keycloak): void {
        this.loginInProgress = true;
        this.localStorageService.setKeycloakTokens({
            access_token: keycloakInstance.token as string,
            refresh_token: keycloakInstance.refreshToken as string,
        });
        const name = keycloakInstance.tokenParsed?.['display_name'] as string | undefined;
        const phone = keycloakInstance.tokenParsed?.['phone'] as string | undefined;
        const email = keycloakInstance.tokenParsed?.['email'] as string;
        const userInfo: UserInfo = {
            name: name,
            phone: phone,
            email: email,
        };
        this.localStorageService.setUserInfo(userInfo);
        this.authService.setUserContext(keycloakInstance.token as string);
        this.authService.isLoggedIn.next(true);
        this.router.navigate(['/providers']);
    }
}
